import React from 'react';
import { ArrowLeft, Settings, Search } from 'lucide-react';
import { UserProfile } from './UserProfile';
import { useAuth } from '../../contexts/AuthContext';

interface UnifiedHeaderProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  showBackButton?: boolean;
  onBackClick?: () => void;
  onShowSettings?: () => void;
  onOpenSearch?: () => void;
}

export const UnifiedHeader: React.FC<UnifiedHeaderProps> = ({
  title = "Ora",
  subtitle = "Prayer Devotion Tracker",
  showBackButton = false,
  onBackClick,
  onShowSettings,
  onOpenSearch,
}) => {
  const { isAuthenticated, user } = useAuth();

  // Get user's first name for welcome message
  const displayName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'User';

  return (
    <div>
    <div className="flex items-center justify-between mb-2 gap-3">
      {/* Left Section: Back Button + Logo + Title */}
      <div className="flex items-center gap-3 min-w-0">
        {/* Back Button */}
        {showBackButton && onBackClick && (
          <button
            onClick={onBackClick}
            className="flex-shrink-0 p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-200"
            title="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}

        {/* Logo Wordmark */}
        <div className="min-w-0">
          <img
            src="/header-logo-light.png"
            alt="Ora"
            className="h-14 sm:h-16 w-auto dark:hidden"
          />
          <img
            src="/header-logo-dark.png"
            alt="Ora"
            className="h-14 sm:h-16 w-auto hidden dark:block"
          />
        </div>
      </div>

      {/* Right Section: Controls */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Search Button */}
        {onOpenSearch && (
          <button
            onClick={onOpenSearch}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-200"
            title="Search prayers"
          >
            <Search className="w-5 h-5" />
          </button>
        )}

        {/* User Profile (includes settings when authenticated) */}
        <UserProfile onShowSettings={onShowSettings} />

        {/* Settings Button (only for non-authenticated users) */}
        {onShowSettings && !isAuthenticated && (
          <button
            onClick={onShowSettings}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-200"
            title="Settings"
          >
            <Settings className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>

    {/* Welcome Message - shows on mobile when logged in */}
    {isAuthenticated && user && (
      <div className="sm:hidden mb-4 ml-2">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Welcome, <span className="font-medium text-gray-800 dark:text-white">{displayName}</span>!
        </p>
      </div>
    )}
    </div>
  );
};